import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
  getRevenueStats, 
  getEstimateStats, 
  getClientStats, 
  getRevenueByMonth,
  getTopClientsByRevenue 
} from '../services/reports';
import { isAdmin, isAdminOrManager } from '../utils/roleHelpers';
import './Dashboard.css';

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [revenueStats, setRevenueStats] = useState(null);
  const [estimateStats, setEstimateStats] = useState(null);
  const [clientStats, setClientStats] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const [revenue, estimates, clients, monthly, top] = await Promise.all([
        getRevenueStats(dateRange.startDate, dateRange.endDate),
        getEstimateStats(dateRange.startDate, dateRange.endDate),
        getClientStats(),
        getRevenueByMonth(12),
        getTopClientsByRevenue(5, dateRange.startDate, dateRange.endDate),
      ]);
      
      setRevenueStats(revenue);
      setEstimateStats(estimates);
      setClientStats(clients);
      setMonthlyRevenue(monthly);
      setTopClients(top);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  return (
    <div className="dashboard-container">
      <div className="dashboard-main">
        <div className="welcome-section">
          <h2>Welcome back!</h2>
          <p>You have successfully logged in to Menlo Atherton Glass.</p>
          {user?.user_metadata?.full_name && (
            <p className="user-name">Hello, {user.user_metadata.full_name}!</p>
          )}
        </div>

        <div className="quick-actions">
          <Link to="/clients" className="action-card">
            <h3>Clients</h3>
            <p>Manage your client database</p>
            <span className="action-arrow">→</span>
          </Link>
          <Link to="/unpaid-invoices" className="action-card">
            <h3>Unpaid Invoices</h3>
            <p>View and manage unpaid invoices</p>
            <span className="action-arrow">→</span>
          </Link>
          <Link to="/open-estimates" className="action-card">
            <h3>Open Estimates</h3>
            <p>Manage open estimates</p>
            <span className="action-arrow">→</span>
          </Link>
        </div>

        {/* Reports Section */}
        <div className="reports-section">
          <div className="reports-header">
            <h2>Reports & Analytics</h2>
            <div className="date-range-filters">
              <div className="filter-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="filter-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">Loading reports...</div>
          ) : (
            <>
              {/* Key Metrics Cards */}
              <div className="metrics-grid">
                {isAdmin(user) && (
                  <div className="metric-card revenue-card">
                    <div className="metric-icon">💰</div>
                    <div className="metric-content">
                      <h3>Total Revenue</h3>
                      <p className="metric-value">${(revenueStats?.totalRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="metric-label">{revenueStats?.paidCount || 0} paid invoices</p>
                    </div>
                  </div>
                )}

                <div className="metric-card unpaid-card">
                  <div className="metric-icon">📋</div>
                  <div className="metric-content">
                    <h3>Unpaid Invoices</h3>
                    <p className="metric-value">${(revenueStats?.unpaidTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className="metric-label">{revenueStats?.unpaidCount || 0} unpaid invoices</p>
                  </div>
                </div>

                <div className="metric-card estimates-card">
                  <div className="metric-icon">📝</div>
                  <div className="metric-content">
                    <h3>Open Estimates</h3>
                    <p className="metric-value">${(estimateStats?.openTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className="metric-label">{estimateStats?.openCount || 0} open estimates</p>
                  </div>
                </div>

                <div className="metric-card clients-card">
                  <div className="metric-icon">👥</div>
                  <div className="metric-content">
                    <h3>Total Clients</h3>
                    <p className="metric-value">{clientStats?.totalClients || 0}</p>
                    <p className="metric-label">Active clients</p>
                  </div>
                </div>
              </div>

              {/* Additional Statistics */}
              <div className="stats-grid">
                {isAdminOrManager(user) && (
                  <div className="stat-card">
                    <h3>Invoice Statistics</h3>
                    <div className="stat-details">
                      <div className="stat-row">
                        <span>Total Invoices:</span>
                        <strong>{revenueStats?.totalCount || 0}</strong>
                      </div>
                      {isAdmin(user) && (
                        <div className="stat-row">
                          <span>Paid:</span>
                          <strong className="stat-paid">{revenueStats?.paidCount || 0}</strong>
                        </div>
                      )}
                      <div className="stat-row">
                        <span>Unpaid:</span>
                        <strong className="stat-unpaid">{revenueStats?.unpaidCount || 0}</strong>
                      </div>
                    </div>
                  </div>
                )}

                <div className="stat-card">
                  <h3>Estimate Statistics</h3>
                  <div className="stat-details">
                    <div className="stat-row">
                      <span>Total Estimates:</span>
                      <strong>{estimateStats?.totalCount || 0}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Open:</span>
                      <strong className="stat-open">{estimateStats?.openCount || 0}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Finalized:</span>
                      <strong>{estimateStats?.finalizedCount || 0}</strong>
                    </div>
                  </div>
                </div>

                {isAdmin(user) && topClients.length > 0 && (
                  <div className="stat-card top-clients-card">
                    <h3>Top Clients by Revenue</h3>
                    <div className="top-clients-list">
                      {topClients.map((client, index) => (
                        <div key={client.clientId} className="top-client-item">
                          <div className="client-rank">#{index + 1}</div>
                          <div className="client-info">
                            <div className="client-name">{client.clientName}</div>
                            <div className="client-stats">
                              ${client.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} • {client.count} invoice{client.count !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isAdmin(user) && monthlyRevenue.length > 0 && (
                  <div className="stat-card monthly-revenue-card">
                    <h3>Revenue by Month (Last 12 Months)</h3>
                    <div className="monthly-revenue-list">
                      {monthlyRevenue.slice(-6).reverse().map((month) => (
                        <div key={month.month} className="monthly-item">
                          <div className="month-label">
                            {new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </div>
                          <div className="month-value">
                            ${month.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="month-count">{month.count} invoice{month.count !== 1 ? 's' : ''}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="info-card">
          <h3>Account Information</h3>
          <div className="info-row">
            <span className="info-label">Email:</span>
            <span className="info-value">{user?.email}</span>
          </div>
          {user?.user_metadata?.full_name && (
            <div className="info-row">
              <span className="info-label">Name:</span>
              <span className="info-value">{user.user_metadata.full_name}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">User ID:</span>
            <span className="info-value">{user?.id}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
